import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CustomRadioGroup } from '../../../../../components/elements/RadioCustom';
import { getAllOption, getSingleEntryFields } from 'services/option';
import { DropdownValueOption } from 'hooks/useGetOptionStartInspection';
import { startCase } from 'lodash';
import { TSingleEntryField } from '../../../../OptionsPage/hook';
import { entriesInclude } from 'utils/entries';

// Available export columns for Location Issues
const AVAILABLE_COLUMNS = [
  'Client Name',
  'Main Location',
  'Sub Location 1',
  'Sub Location 2',
  'Vertical Level',
  'Location',
  'Description',
  'Validated',
  'Action By',
  'Action By Date',
  'Status',
  'Date Resolved',
  'Date Created',
  'Last Modified',
  'Photos'
];

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '80vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(3),
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
}));

const ColumnGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(1),
  maxHeight: '50vh',
  overflowY: 'auto',
  marginBottom: theme.spacing(2)
}));

interface RoomIssuesExportColumnModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (
    type: 'pdf' | 'xlsx',
    imageOption: string,
    columns: string[],
    dropdownOptionColumns?: number[],
    singleFieldColumns?: number[]
  ) => void;
}

export const RoomIssuesExportColumnModal: React.FC<RoomIssuesExportColumnModalProps> = ({
  open,
  onClose,
  onExport
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(AVAILABLE_COLUMNS);
  const [imageOption, setImageOption] = useState('1st');
  const [singleFields, setSingleFields] = useState<TSingleEntryField[]>([]);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownValueOption[]>([]);
  const [selectedDropdownOptions, setSelectedDropdownOptions] = useState<number[]>([]);
  const [selectedSingleFields, setSelectedSingleFields] = useState<number[]>([]);

  const fetchDropdownOptions = useCallback(async () => {
    const raw = (await getAllOption()) as { metadata: DropdownValueOption[] };
    const filtered = raw.metadata.filter((item) =>
      item.options.some((op) => op.type === 'ROOM_LEVEL_ISSUE')
    );
    setDropdownOptions(filtered);
  }, []);

  const fetchSingleFields = useCallback(async () => {
    const data = (await getSingleEntryFields()) as { singleEntryFields: TSingleEntryField[] };
    const filtered = data.singleEntryFields.filter((item) =>
      entriesInclude(item.entries, 'ROOM_LEVEL_ISSUE')
    );
    setSingleFields(filtered);
  }, []);

  const handleSelectAll = useCallback(() => {
    const dropdownOptionsColumns = dropdownOptions.map((item) => item?.name);
    const singleFieldsColumns = singleFields.map((item) => item?.name);
    setSelectedDropdownOptions(dropdownOptions.map((item) => item.id));
    setSelectedSingleFields(singleFields.map((item) => item.id));
    setSelectedColumns([...AVAILABLE_COLUMNS, ...dropdownOptionsColumns, ...singleFieldsColumns]);
  }, [dropdownOptions, singleFields]);

  useEffect(() => {
    fetchSingleFields();
    fetchDropdownOptions();
  }, [fetchSingleFields, fetchDropdownOptions]);
  useEffect(() => {
    if (open) {
      handleSelectAll();
    }
  }, [open, handleSelectAll]);

  const handleColumnToggle = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column) ? prev.filter((col) => col !== column) : [...prev, column]
    );
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
    setSelectedDropdownOptions([]);
    setSelectedSingleFields([]);
  };

  const handleExport = (type: 'pdf' | 'xlsx') => {
    if (selectedColumns.length === 0) {
      alert('Please select at least one column to export.');
      return;
    }
    const staticColumns: string[] = [];
    selectedColumns.forEach((column) => {
      if (AVAILABLE_COLUMNS.includes(column)) {
        staticColumns.push(column);
      }
    });
    onExport(type, imageOption, staticColumns, selectedDropdownOptions, selectedSingleFields);
    onClose();
  };

  const isAllSelected = selectedColumns.length === AVAILABLE_COLUMNS.length;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent>
        <Stack direction="row" justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="h6" component="h2" gutterBottom>
            Select Export Columns
          </Typography>

          <CustomRadioGroup
            value={imageOption || '1st'}
            onChange={(val) => setImageOption(val)}
            options={[
              { value: '1st', label: '1st Only' },
              { value: 'all', label: 'All Images' }
            ]}
          />
        </Stack>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="end">
            <Button
              sx={{ padding: '16px 12px', height: 'fit-content' }}
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              disabled={isAllSelected}>
              Select All
            </Button>
            <Button
              sx={{ padding: '16px 12px', height: 'fit-content' }}
              size="small"
              variant="outlined"
              onClick={handleDeselectAll}
              disabled={selectedColumns.length === 0}>
              Deselect All
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {selectedColumns.length} of{' '}
            {AVAILABLE_COLUMNS.length + dropdownOptions?.length + singleFields?.length} columns
            selected
          </Typography>
        </Box>

        <ColumnGrid>
          {AVAILABLE_COLUMNS.map((column) => (
            <FormControlLabel
              key={column}
              control={
                <Checkbox
                  checked={selectedColumns.includes(column)}
                  onChange={() => handleColumnToggle(column)}
                  size="small"
                />
              }
              label={column}
              sx={{ fontSize: '0.875rem' }}
            />
          ))}
          {dropdownOptions.map((field, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={selectedColumns.includes(field?.name || '')}
                  onChange={() => {
                    if (field) {
                      handleColumnToggle(field.name);
                      setSelectedDropdownOptions((prev) =>
                        prev.includes(field.id)
                          ? prev.filter((col) => col !== field.id)
                          : [...prev, field.id]
                      );
                    }
                  }}
                  size="small"
                />
              }
              label={startCase(field.name)}
              sx={{ fontSize: '0.875rem' }}
            />
          ))}
          {singleFields.map((field, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={selectedColumns.includes(field?.name || '')}
                  onChange={() => {
                    if (field) {
                      handleColumnToggle(field.name);
                      setSelectedSingleFields((prev) =>
                        prev.includes(field.id)
                          ? prev.filter((col) => col !== field.id)
                          : [...prev, field.id]
                      );
                    }
                  }}
                  size="small"
                />
              }
              label={startCase(field.name)}
              sx={{ fontSize: '0.875rem' }}
            />
          ))}
        </ColumnGrid>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleExport('xlsx')}
            disabled={selectedColumns.length === 0}>
            Export Spreadsheet
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleExport('pdf')}
            disabled={selectedColumns.length === 0}>
            Export PDF
          </Button>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
